/**
 * Where the backend lives — and how to switch between them.
 *
 * SINGLE SOURCE OF TRUTH. Everything that talks to the backend imports from
 * here, so the hostname is defined in exactly one place.
 *
 *   laravel  https://backend.toplistbot.com   the original Laravel 8 app
 *   nextjs   https://behind.toplistbot.com    the Next.js port  (CURRENT DEFAULT)
 *   local    http://localhost:8000            a backend dev server on this machine
 *
 * The two deployed backends share JWT_SECRET and produce identical token
 * claims, so a session is valid on BOTH. Switching — in either direction —
 * does not sign anyone out.
 *
 * HOW TO SWITCH
 * -------------
 * Append ?backend=… to any panel URL. The choice is remembered in localStorage,
 * so it survives reloads and applies to every subsequent request:
 *
 *   ?backend=nextjs    the deployed Next.js backend
 *   ?backend=laravel   the original Laravel backend
 *   ?backend=local     a backend dev server on localhost:8000
 *   ?backend=reset     clear the override, fall back to DEFAULT_BACKEND
 *
 * The override applies on localhost too, which is how you point a local panel
 * at a DEPLOYED backend — the only way to exercise the real thing (production
 * data, real TLS, real CORS) without deploying the frontend first.
 *
 * >> HEADS UP: with the default below, a panel running on localhost talks to
 * >> the LIVE backend and therefore the LIVE database. Anything you do in it —
 * >> placing an order, editing one, spending tokens — is real.
 *
 * To change what everyone gets, edit DEFAULT_BACKEND and deploy.
 */

const BACKENDS = {
  laravel: "https://backend.toplistbot.com",
  nextjs: "https://behind.toplistbot.com",
  local: "http://localhost:8000",
};

/** What you get without an override. Change this one line to switch or roll back. */
const DEFAULT_BACKEND = "nextjs";

const STORAGE_KEY = "backend_override";

/**
 * Resolves the override from the query string, persisting it, then falls back
 * to whatever was stored previously.
 *
 * Reads `location.search` AND `location.hash`, because the panel is a hash
 * router — `/#/orders?backend=local` puts the query inside the fragment, where
 * `location.search` cannot see it.
 */
function resolveOverride() {
  let requested = new URLSearchParams(window.location.search).get("backend");

  if (!requested && window.location.hash.includes("?")) {
    const afterQuestionMark = window.location.hash.slice(window.location.hash.indexOf("?") + 1);
    requested = new URLSearchParams(afterQuestionMark).get("backend");
  }

  try {
    if (requested === "reset") {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (requested && BACKENDS[requested]) {
      window.localStorage.setItem(STORAGE_KEY, requested);
      return requested;
    }
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored && BACKENDS[stored] ? stored : null;
  } catch {
    // Private mode or storage disabled — honour the query param for this page
    // load rather than breaking every request.
    return requested && BACKENDS[requested] ? requested : null;
  }
}

/** Which backend this browser is talking to: 'laravel', 'nextjs' or 'local'. */
export const ACTIVE_BACKEND = resolveOverride() ?? DEFAULT_BACKEND;

/**
 * Scheme + host, no trailing slash and no /api.
 *
 * Used by the links that point straight at the backend rather than calling the
 * JSON API: the OAuth redirects and the password-reset form.
 */
export const BACKEND_ORIGIN = BACKENDS[ACTIVE_BACKEND];

const isLocalPanel =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

/** Where the panel itself lives — the OAuth callback target. */
export const FRONTEND_ORIGIN = isLocalPanel
  ? window.location.origin
  : "https://app.toplistbot.com";

/*
 * `FORGOT_PASSWORD_URL` used to live here, pointing at the backend's
 * server-rendered /forget-password form. Password reset is now a panel route,
 * so every link goes through the router (`to="/forgot-password"`) and this
 * constant had no consumers left. Removed rather than repointed: a stale export
 * describing an architecture we no longer have is the kind of thing someone
 * reaches for later and quietly sends users back to the old page.
 */

// Makes the current choice visible, so "which backend am I actually on?" is
// answerable from the console instead of by reading localStorage by hand.
// eslint-disable-next-line no-console
console.info(
  `[toplistbot] backend: ${ACTIVE_BACKEND} (${BACKEND_ORIGIN})` +
    (ACTIVE_BACKEND !== "local" && isLocalPanel ? "  ** LIVE DATA **" : "") +
    ` — switch with ?backend=nextjs | laravel | local | reset`,
);

const API_URL = `${BACKEND_ORIGIN}/api`;

export default API_URL;
